import { useState, useMemo, useEffect, useContext } from 'react'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedBorderButton from '../components/buttons/RoundedBorderButton'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import HarborSkillsTable from '../components/tables/HarborSkillsTable'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const UserDetails = () => {
    const { id } = useParams()

    const [showDisableUserModal, setShowDisableUserModal] = useState(false)
    const [showEnableUserModal, setShowEnableUserModal] = useState(false)
    const [showRemoveUserModal, setShowRemoveUserModal] = useState(false)
    const [user, setUser] = useState(null)
    const [role, setRole] = useState(null)
    const [usageTypes, setUsageTypes] = useState([])

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role === Constants.Roles.PORTADMIN || loggedUser.role === Constants.Roles.ADMINIT) {
                getDropdowns()
                getUserDetails()
            } else {
                navigate('/')
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get dropdowns request
    const getDropdowns = () => {
        fetch('/api/Boat/Dropdowns', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                const arrayUsages = jsonRes.usageTypes
                setUsageTypes(arrayUsages)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setUsageTypes([])
                }, 1000)   
            }
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setUsageTypes([])
            }, 1000)
        })
    }

    // Handle get user details
    const getUserDetails = () => {
        fetch(`/api/Backoffice/${id}/Details`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()

                    setUser(jsonRes)
                    setRole(jsonRes.roleId)
                } else {
                    showErrorToast(strings.getUserDetailsError)
                    setUser(null)
                    setRole(null)
                }
                
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setUser(null)
                setRole(null)
            }, 1000)
        })
    }

    const columnsBoats = useMemo(() => [
        {
            name: strings.name2,
            selector: row => row.name,
            sortable: true
        },
        {
            name: strings.registration,
            selector: row => row.registration,
            sortable: true
        },
        {
            name: strings.grossTonnage + strings.grt2,
            selector: row => row.grossTonnage,
            sortable: true
        },
        {
            name: strings.outOutLength2,
            selector: row => row.length,
            sortable: true
        },
        {
            name: strings.typeOfUse,
            selector: row => showUsageTypeText(usageTypes, row.usageTypeId),
            sortable: true
        },
    ], [usageTypes])

    // Show disable user modal
    const onClickDisable = () => {
        setShowDisableUserModal(true)
    }

    // Handles disabled user
    const disableUser = () => {
        setShowDisableUserModal(false)
        setIsLoading(true)

        // Disable user
        fetch(`/api/BackOffice/${id}/DeactivateUser`, {
            method:'PUT',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
        
                if (response.status === 200) {
                    setIsLoading(true)
                    getUserDetails()
                } else if (response.status === 404) {
                    showErrorToast(strings.userNotFound)
                } else if (response.status === 401) {
                    showErrorToast(strings.notAuthorized)
                } else {
                    showErrorToast(strings.disableUserError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })  
    }

    // Show enable user modal
    const onClickEnable = () => {
        setShowEnableUserModal(true)
    }

    // Handles enable user
    const enableUser = () => {
        setShowEnableUserModal(false)
        setIsLoading(true)

        // Enable user
        fetch(`/api/BackOffice/${id}/ActivateUser`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
              setIsLoading(false)
    
              if (response.status === 200) {
                setIsLoading(true)
                getUserDetails()
              } else if (response.status === 404) {
                showErrorToast(strings.userNotFound)
              } else if (response.status === 401) {
                showErrorToast(strings.notAuthorized)
              } else {
                showErrorToast(strings.enableUserError)
              }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
              setIsLoading(false)
              showErrorToast(error)
            }, 1000)
        })
    }

    // Show remove user modal
    const onClickRemove = () => {
        setShowRemoveUserModal(true)
    }

    // Handles remove user
    const removeUser = () => {
        setShowRemoveUserModal(false)
        setIsLoading(true)

        // Remove user
        fetch(`/api/BackOffice/${id}/DeleteUser`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
              setIsLoading(false)
    
              if (response.status === 200) {
                navigate('/backoffice-users')
              } else if (response.status === 404) {
                showErrorToast(strings.userNotFound)
              } else {
                showErrorToast(strings.deleteUserError)
              }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
              setIsLoading(false)
              showErrorToast(error)
            }, 1000)
        })
    }

    // Navigate to update user
    const onClickUpdate = () => {
        switch(role) {
            case 1: navigate(`/edit-client/${user.id}`)
            break

            case 2: navigate(`/edit-dealer/${user.id}`) 
            break

            case 3: navigate(`/edit-harbor-technician/${user.id}`)
            break

            case 4: navigate(`/edit-harbor-admin/${user.id}`)
            break

            default: return
        }
    }

    // Check the usage type and show matching text
    const showUsageTypeText = (usageTypes, usageTypeId) => {
        const usageType = usageTypes && usageTypes.find((usageType) => usageType.id === usageTypeId)
        return usageType && usageType.name
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='flex flex-col px-[12.5rem] min-h-screen mb-6'>
                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.userManagement} / {user && user.name} {role && Constants.getRoleNameById(role)}</p>
                        </div>

                        <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>

                        <div className='flex flex-row mt-6'>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{role && (role === Constants.Roles.CLIENT ? strings.accessEmail : strings.email)}</p>
                                <p className='font-montserrat text-xl font-semibold'>{user && user.email}</p>
                            </div>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{strings.name}</p>
                                <p className='font-montserrat text-xl font-semibold'>{user && user.name}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-9'>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{role && (role === Constants.Roles.CLIENT ? strings.nif : strings.affectedShipyard)}</p>
                                <p className='font-montserrat text-xl font-semibold'>{user && role && (role === Constants.Roles.CLIENT ? user.nif : user.harborName)}</p>
                            </div>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{role && (role === Constants.Roles.CLIENT ? strings.address : strings.username)}</p>
                                <p className='font-montserrat text-xl font-semibold'>{user && role && (role === Constants.Roles.CLIENT ? user.address : user.username)}</p>
                            </div>
                        </div>

                        { role && role === Constants.Roles.CLIENT ?
                            <div className='flex flex-row mt-9'>
                                <div className='flex flex-col w-1/2'>
                                    <p className='font-montserrat text-sm'>{strings.zipCode }</p>
                                    <p className='font-montserrat text-xl font-semibold'>{user && user.postalCode}</p>
                                </div>
                                <div className='flex flex-col w-1/2'>
                                    <p className='font-montserrat text-sm'>{strings.locality}</p>
                                    <p className='font-montserrat text-xl font-semibold'>{user && user.locality}</p>
                                </div>
                            </div>
                        : null }
                    
                        { role && (role === Constants.Roles.DEALER) ? 
                            <div className='mt-9'>
                                <p className='font-montserrat text-sm'>{strings.entity}</p>
                                <p className='font-montserrat text-xl font-semibold'>{user && user.entityName}</p>
                            </div>
                        : null }

                        { role && role === Constants.Roles.CLIENT ? 
                            <>
                                <p className='font-montserrat text-xl font-bold text-navy mt-11 mb-6'>{strings.boats}</p>

                                { user && user.boats.length > 0 ?
                                    <HarborSkillsTable
                                        columns={columnsBoats}
                                        data={user && user.boats} 
                                    />
                                :   <p className='font-montserrat text-neutral5'>{strings.noInfo}</p> }
                            </>           
                        : null }

                        <div className='flex flex-row items-center space-x-4 mt-16'>

                            { user && user.active ?
                                <div className='flex w-60'>
                                    <RoundedButton 
                                        onClick={onClickUpdate}
                                        text={strings.updateUser}
                                        height={48}
                                        bgColor='#007EB4'
                                    />
                                </div>
                            : null }
                            
                            <div className='flex w-60'>
                                <RoundedBorderButton
                                    text={user && (user.active ? strings.disableUser : strings.enableUser)}
                                    onClick={user && (user.active ? onClickDisable : onClickEnable)}
                                    height={48}
                                    fontWeight='600'
                                />
                            </div>


                            <div className='flex w-60'>
                                <RoundedButton 
                                    onClick={onClickRemove}
                                    text={strings.removeUser}
                                    height={48}
                                    bgColor='#E85F5C'
                                />
                            </div>
                        </div>
                    </>
                }

            </div>

            <TwoButtonsModal 
                open={showDisableUserModal}
                onClose={() => setShowDisableUserModal(false)}
                title={strings.disableUser}
                message={strings.disableUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.disable}
                onClick={disableUser}
            />

            <TwoButtonsModal 
                open={showEnableUserModal}
                onClose={() => setShowEnableUserModal(false)}
                title={strings.enableUser}
                message={strings.enableUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.enable}
                onClick={enableUser}
            />

            <TwoButtonsModal 
                open={showRemoveUserModal}
                onClose={() => setShowRemoveUserModal(false)}
                title={strings.removeUser}
                message={strings.removeUserMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.remove}
                onClick={removeUser}
            />

            <ToastContainer />
        </>
    )
}

export default UserDetails